import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { useProfile } from '../../init/use-enforce-profile';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

export const LightDebtorSchema = z.object({
  id: z.coerce.string(),
  fullName: z.string(),
  externalId: z.coerce.string().nullable().optional(),
  csvId: z.string().nullable().optional(),
});

const LightDebtorsSchema = z.array(LightDebtorSchema);

export type LightDebtors = z.infer<typeof LightDebtorsSchema>;

export type LightDebtor = z.infer<typeof LightDebtorSchema>;

type LightDebtorsVariables = {
  query: string;
  externalId?: string;
};

export async function loadLightDebtorsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { query, externalId } = queryKey[1] as LightDebtorsVariables;
  const { data } = await instance.get('/debtors/light', {
    params: { query, ...(externalId != null && { external_id: externalId }) },
  });

  return LightDebtorsSchema.parse(data);
}

/**
 * **This request is capped at the first 1000 results**
 */
const emptyLightDebtors: LightDebtors = [];

export const useLoadLightDebtors = (
  variables: LightDebtorsVariables,
  options: { enabled: boolean } = { enabled: true },
) => {
  const axiosInstance = useAxiosInstance();
  const profile = useProfile();

  const queryResult = useQuery<LightDebtors, ApiError>({
    queryKey: ['light-debtors', variables],
    queryFn: context => loadLightDebtorsQueryFn(axiosInstance, context),
    placeholderData: data => keepPreviousData(data) ?? emptyLightDebtors,
    enabled: options.enabled && profile.hasAcceptedGc,
  });

  return addResourceNameToQueryResult<LightDebtors, unknown, 'lightDebtors', 'PreventUndefined'>(
    'lightDebtors',
    queryResult,
    emptyLightDebtors,
  );
};
